import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

// DEPENDENCIES
import isObject from 'lodash/isObject';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { sessionActions } from 'actions.js';
import { apiFetch, makePath, doCallback } from 'functions.js';

// GLOBAL PARTS
import Preloader from 'parts/preloader/Preloader';

// CONTEXT
export const RecordContext = createContext(null);
export const useRecordContext = () => useContext(RecordContext);

// FUNCTIONS
const getDealershipID = (error = []) => error.reduce((acc, cur) => isObject(cur) && cur.validDealerId ? cur.validDealerId : acc, undefined);

// REDUCER
const reducer = (state, { type, ...action }) => {

  // RECORD
  switch (type) {
    case 'fetchData':

      return {
        ...state,
        loading: true,
      }
    case 'loadData':

      const { result } = action;

      return {
        ...state,
        loading: false,
        record: result || action
      };
    case 'updateRecord':
      return {
        ...state,
        record: { ...(state.record || {}), ...action }
      }
    case 'fetchFailed':

      return {
        ...state,
        loading: false
      }
    default:
      throw new Error();
  }
}

// MAIN COMPONENT
export const getRecordData = (
  Component,
  {
    fetchArgs = {}
  } = {}
) => compose(
  connect(
    ({ dealership }) => ({ dealership }),
    { setStore: sessionActions.setStore }
  ),
  withRouter
)(({
  endpoints = {},
  recordIDKey,
  recordLabel = 'record',
  usePreloader = false,
  // REACT ROUTER
  navigate,
  location,
  onRecordLoaded,
  // REDUX STORE
  dealership,
  // REDUX DISPATCH
  setStore,
  // REST
  ...props
}) => {
  const { recordID } = useParams();

  // STATE
  const [ context, updateContext ] = useReducer(reducer, { loading: true });

  // RECORD FETCH
  const endpointsMemo = useMemo(
    () => ({
      get: endpoints.get || endpoints.single,
      post: endpoints.post || endpoints.update || endpoints.single,
      put: endpoints.put || endpoints.update || endpoints.single,
      remove: endpoints.delete || endpoints.update || endpoints.single,
    }),
    [endpoints]
  )
  const errorMessage = useCallback(
    ({ errors } = {}, status) => {
      if (status === 403) {
        return `You do not have access to the dealership with which the requested ${recordLabel} is associated.`;
      }
      if (getDealershipID(errors)) {
        return;
      }
      return `Unable to load ${recordLabel}.`;
    },
    [recordLabel]
  )
  const handleError = useCallback(
    ({ errors } = {}, status) => {
      if (status === 403) {
        navigate(makePath(INTERFACE.dashboard))
        return;
      }
      const make_dealer_id = getDealershipID(errors);
      if (make_dealer_id) {
        apiFetch({
          method: 'POST',
          endpoint: ENDPOINTS.session.chooseStore,
          params: {
            make_dealer_id
          },
          onSuccess: setStore,
          loadingMessage: 'Changing dealership',
          successMessage: 'Dealership changed.',
          errorMessage: 'Unable to change dealership.'
        })
        return;
      }
      else updateContext({ type: 'fetchFailed' })
    },
    [setStore, updateContext, navigate]
  )
  const fetchRecord = useCallback(
    ({ loading = true, ...args } = {}) => {

      const endpoint = fetchArgs.endpoint || endpointsMemo.get;

      if (!endpoint || !recordID)
        return;

      apiFetch({
        ...fetchArgs,
        endpoint: makePath(endpoint, recordID),
        loadingMessage: `Loading ${recordLabel}`,
        errorMessage,
        onFetch: () => {
          if (loading) updateContext({ type: 'fetchData' })
        },
        onSuccess: data => {
          updateContext({
            type: 'loadData',
            ...data
          })
          doCallback(onRecordLoaded, data);
        },
        onError: (error, status) => {
          handleError(error, status);
        },
        ...args
      })
    },
    [endpointsMemo, recordLabel, recordID, errorMessage, handleError, updateContext, onRecordLoaded]
  )
  const refetchRecord = useCallback(
    (args = {}) => fetchRecord({ loading: false, ...args }),
    [fetchRecord]
  )

  // FETCH PATH/DEPENDENCY LISTENER
  useEffect(
    () => {
      const abortController = new AbortController(); 
      fetchRecord({
        abortController 
      });
      return () => {
        abortController.abort();
      }
    },
    [fetchRecord]
  )

  // RENDER
  return (
    <RecordContext.Provider value={{
      endpoints: endpointsMemo,
      recordID,
      recordIDKey,
      recordLabel,
      ...context,
      updateContext,
      refetchRecord
    }}>
      <Preloader loading={usePreloader && recordID && endpointsMemo.get && context.loading} useIcon>
        <Component {...props} />
      </Preloader>
    </RecordContext.Provider>
  )
})
